import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getLandingContent } from '@/mocks/landingData';

export default function Navbar() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const content = getLandingContent(i18n.language);
  const currentLanguage = i18n.language.startsWith('ru') ? 'ru' : 'kk';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const handleLanguageChange = (language: 'kk' | 'ru') => {
    window.localStorage.setItem('mesutronicLanguage', language);
    void i18n.changeLanguage(language);
    setMobileOpen(false);
  };

  const renderLanguageSwitcher = (light = false) => (
    <div
      className={`inline-flex items-center rounded-md border text-xs font-semibold overflow-hidden ${
        light || scrolled ? 'border-gray-200 bg-gray-50 text-gray-600' : 'border-white/25 bg-white/10 text-white'
      }`}
    >
      {(['kk', 'ru'] as const).map((language) => (
        <button
          key={language}
          type="button"
          onClick={() => handleLanguageChange(language)}
          className={`px-3 py-2 transition-colors ${
            currentLanguage === language
              ? 'bg-accent text-white'
              : light || scrolled
                ? 'hover:bg-white hover:text-gray-900'
                : 'hover:bg-white/15'
          }`}
          aria-pressed={currentLanguage === language}
        >
          {language === 'kk' ? 'Қаз' : 'Рус'}
        </button>
      ))}
    </div>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white border-b border-gray-200'
          : 'bg-transparent'
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-10 lg:px-16 h-16 md:h-20">
        <a
          href="#"
          className={`text-lg md:text-xl font-semibold tracking-tight transition-colors ${
            scrolled ? 'text-gray-900' : 'text-white'
          }`}
        >
          TZK East
        </a>

        <div className="hidden md:flex items-center gap-8 lg:gap-10">
          {content.nav.links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className={`text-sm font-medium transition-colors hover:text-red-600 cursor-pointer ${
                scrolled ? 'text-gray-600' : 'text-white/90'
              }`}
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          {renderLanguageSwitcher()}
          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, '#contact')}
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
          >
            {content.nav.consultation}
          </a>
        </div>

        <button
          type="button"
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
            scrolled ? 'text-gray-900' : 'text-white'
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? content.nav.closeLabel : content.nav.menuLabel}
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl w-6 h-6 flex items-center justify-center`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-card">
          <div className="flex flex-col px-6 py-5 gap-3">
            {content.nav.links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-gray-700 text-sm font-medium py-2 cursor-pointer hover:text-red-600 transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-1">
              {renderLanguageSwitcher(true)}
            </div>
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, '#contact')}
              className="inline-flex items-center justify-center bg-accent hover:bg-accent-hover text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap mt-2"
            >
              {content.nav.consultation}
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
